import React, { useState } from 'react'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Alert, Box, Checkbox, FormControlLabel, Typography } from '@mui/material'
import DefaultModalActions from '../../../../components/GenericModal/DefaultModalActions'
import { ButtonType } from '../../../../components/GenericButton/styles'
import { formatDateByUserTimeZone } from '../../../../helpers/formatDateByUserTimeZone'
import { BULK_ACTION_WARN_THRESHOLD } from '../bulkSectionStatusActions'

/**
 * The confirm step of Bulk Go Live.
 *
 * No date inputs, unlike the reopen confirm: the endpoint takes only the selection. This is the
 * per-row **Go Live Now** action in bulk, with the same semantics — which is exactly why it needs
 * spelling out. Going live takes effect immediately, so the surveys start collecting responses from
 * the moment this is confirmed, and there is no scheduling step. The timestamp below is that
 * moment, and the acknowledgement is required because the action cannot be undone from here: each
 * section would have to be closed individually.
 *
 * A section scheduled to start later is *moved* to start now, which both the alert **and the
 * acknowledgement** state — the acknowledgement is the sentence the admin actually ticks, so it is
 * the wrong one to leave loose. The backend does that in the helper both this and the per-row
 * action share, clamping to the earliest instant the Survey Project allows — so on a project whose
 * own start is still in the future, the section starts when the project does rather than now
 * (Backend #2130, Behavior Change 1). Deliberately not patched from here: a client-side date write
 * would let the bulk and per-row paths date sections differently.
 *
 * Follows the acknowledgement pattern in `UpdateTemplateRevisionModal`.
 *
 * Spec: docs/specs/active/bulk-reopen-closed-section-surveys/spec.md
 */

interface BulkGoLiveConfirmProps {
  selectionCount: number
  onConfirm: () => Promise<void> | void
  onCancel: () => void
  isSubmitting?: boolean
}

const BulkGoLiveConfirm: React.FC<BulkGoLiveConfirmProps> = ({
  selectionCount,
  onConfirm,
  onCancel,
  isSubmitting = false
}) => {
  const [hasAgreed, setHasAgreed] = useState(false)
  // Captured once, when the step opens, so the displayed moment does not drift while the admin
  // reads it. It is a preview of "now", not a value sent to the backend.
  const [goLiveAt] = useState(() => new Date())

  return (
    <Box>
      <Alert severity='info' icon={<InfoOutlinedIcon />} sx={{ mb: 3 }}>
        <Typography fontWeight='bold' fontSize='0.875rem'>
          These course section surveys go live immediately — there is no scheduled start. Any that
          were scheduled to start later start now instead, or when the Survey Project itself starts
          if that is still in the future.
        </Typography>
      </Alert>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 2, rowGap: 0.5, mb: 3 }}>
        <Typography variant='body2' color='text.secondary'>Course Section Surveys</Typography>
        <Typography variant='body2' fontWeight={500}>{selectionCount.toLocaleString()}</Typography>
        <Typography variant='body2' color='text.secondary'>Going live</Typography>
        <Typography variant='body2' fontWeight={500}>
          {formatDateByUserTimeZone(goLiveAt.toISOString(), 'MM/dd/yyyy @ h:mm a')} (now)
        </Typography>
      </Box>

      {selectionCount > BULK_ACTION_WARN_THRESHOLD && (
        <Typography variant='body2' fontWeight={500} role='alert' sx={{ mb: 2 }}>
          This starts {selectionCount.toLocaleString()} surveys and cannot be undone. Go back and narrow the filter if that is more than you intended.
        </Typography>
      )}

      <FormControlLabel
        sx={{ alignItems: 'flex-start', mb: 3, ml: 0 }}
        control={
          <Checkbox
            checked={hasAgreed}
            onChange={(e) => { setHasAgreed(e.target.checked) }}
            sx={{ pt: 0, pl: 0, mr: 1 }}
          />
        }
        label={
          <Typography variant='body2'>
            I understand {selectionCount === 1 ? 'this course section survey' : `these ${selectionCount.toLocaleString()} course section surveys`} will start collecting student responses immediately — or when the Survey Project starts, if that is still in the future — and that going live cannot be undone from here.
          </Typography>
        }
      />

      <DefaultModalActions
        destructiveButtonProps={{ onClick: onCancel }}
        confirmButtonProps={{
          onClick: () => { void onConfirm() },
          text: 'Go Live',
          buttonType: ButtonType.Primary,
          disabled: !hasAgreed,
          loading: isSubmitting
        }}
      />
    </Box>
  )
}

export default BulkGoLiveConfirm
